// Blocks to email HTML. One renderer for three places: the builder canvas
// (each section in its own shadow root), the inbox preview (an iframe) and
// the campaign payload (emailHtml, sent with merge tags intact).
import { resolveTheme, themeSpecFromBlocks, fontFallback, googleFontsHref } from './theme'
import { SECTIONS, isSectionType, footerHtml } from './sections'
import { legacyHtml } from './legacy'
import { esc } from './html'

export const isV2Footer = (b) => !!(b && b.type === 'footer' && b.content && (b.content.style === 'v2' || b.content.theme))
// An email made with the new sections carries its theme on a v2 footer. Old
// emails (classic blocks, no theme) keep the classic builder and renderer.
export const isThemedEmail = (blocks) => (blocks || []).some(isV2Footer)
// Blocks the canvas shows as email HTML (new sections and the themed footer).
export const isHtmlBlock = (b) => !!(b && (isSectionType(b.type) || isV2Footer(b)))

// Old emails (no theme on the footer) keep their own colours; they only need
// a neutral canvas behind them.
export const themeForBlocks = (blocks, overrideSpec) => {
  const spec = overrideSpec || themeSpecFromBlocks(blocks)
  const theme = resolveTheme(spec || { look: 'letter' })
  if (!spec) theme.palette = { ...theme.palette, canvas: '#F2F2F4' }
  return theme
}

export const renderBlockHtml = (block, theme, { builder = false } = {}) => {
  if (!block || !block.type) return ''
  const c = block.content || {}
  try {
    if (isSectionType(block.type)) return SECTIONS[block.type].html(c, { theme, builder })
    if (isV2Footer(block)) return footerHtml(c, { theme, builder })
    return legacyHtml(block, theme, { builder })
  } catch (e) {
    // One bad block must never cost the salon the whole email
    // eslint-disable-next-line no-console
    console.error('email block render failed', block.type, e)
    return ''
  }
}

// Phone rules. The same declarations run as a media query in the email and
// as a container query in the builder (so the mobile preview is real).
const MOBILE = [
  '.es-px{padding-left:24px !important;padding-right:24px !important}',
  '.es-col{max-width:100% !important}',
  '.es-col .es-img{max-width:100% !important}',
  '.es-colpad{padding-left:0 !important;padding-right:0 !important}',
  '.es-stackgap .es-colpad{padding-bottom:26px !important}',
  '.es-gallery .es-col{max-width:50% !important}',
  '.es-g3 .es-col{max-width:33.33% !important}',
  '.es-gallery .es-colpad{padding-left:4px !important;padding-right:4px !important}',
  '.es-dxxl{font-size:52px !important;line-height:1 !important;letter-spacing:-0.04em !important}',
  '.es-feature-words{font-size:42px !important}',
  '.es-cardpad{padding-left:22px !important;padding-right:22px !important}',
  '.es-wm1{font-size:96px !important}', '.es-wm2{font-size:74px !important}', '.es-wm3{font-size:54px !important}', '.es-wm4{font-size:40px !important}', '.es-wm5{font-size:30px !important}',
  '.es-dxl{font-size:40px !important;line-height:1.05 !important}',
  '.es-dl{font-size:34px !important;line-height:1.08 !important}',
  '.es-dm{font-size:28px !important;line-height:1.14 !important}',
  '.es-ds{font-size:23px !important;line-height:1.2 !important}',
  '.es-offer{font-size:48px !important}',
  '.es-voucher-value{font-size:54px !important}',
  '.es-hero{padding-top:44px !important;padding-bottom:44px !important}',
  '.es-ticket{padding:32px 22px !important}',
  '.es-voucher{padding:26px 24px !important}',
  '.es-quote{font-size:21px !important}',
  '.es-btnt{width:100% !important}',
  '.es-btn{display:block !important}',
  '.es-outer{padding:0 !important}',
]

const BASE_CSS = [
  ':root{color-scheme:light only;supported-color-schemes:light only}',
  'body{margin:0 !important;padding:0 !important;width:100% !important;-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%}',
  'table,td{border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt}',
  'img{border:0;line-height:100%;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic}',
  'a[x-apple-data-detectors]{color:inherit !important;text-decoration:none !important;font-size:inherit !important;font-family:inherit !important;font-weight:inherit !important;line-height:inherit !important}',
  'u + #body a{color:inherit;text-decoration:none;font-size:inherit;font-family:inherit;font-weight:inherit;line-height:inherit}',
  '#MessageViewBody a{color:inherit;text-decoration:none;font-size:inherit;font-family:inherit;font-weight:inherit;line-height:inherit}',
  'h1,h2,h3,p{margin:0}',
].join('')

// Phones: anything narrower than the 600px email (the same moment the
// builder's container query switches, so the canvas and inbox agree)
export const emailStyleBlock = () => `${BASE_CSS}@media only screen and (max-width:599px){${MOBILE.join('')}}`

// Styles for one block in the builder: isolated in a shadow root, sized by
// the host (a container), so it can't pick up the app's CSS and the phone
// preview switches layout exactly like a phone would.
export const canvasShadowCss = () => [
  ':host{all:initial;display:block;container-type:inline-size;container-name:es}',
  'table,td{border-collapse:collapse}',
  'img{border:0;outline:none;text-decoration:none}',
  'h1,h2,h3,p{margin:0}',
  'a{cursor:pointer}',
  // A section narrower than the 600px email is what a phone shows (the
  // email's own rule is on the screen width at the same 599px)
  `@container es (max-width:599px){${MOBILE.join('')}}`,
].join('')

// Merge tags as a client would see them, for previews only.
export const SAMPLE_TAGS = {
  FirstName: 'Sophie',
  SalonName: 'Your salon',
  SalonAddress: '12 High Street, Bristol BS1 4DJ',
  CompanyDetails: '',
  BookingLink: '#',
  GiftCardLink: '#',
  ShopLink: '#',
  UnsubscribeLink: '#',
  PreferencesLink: '#',
}
export const applySample = (html, sample = {}) => {
  const values = { ...SAMPLE_TAGS, ...Object.fromEntries(Object.entries(sample).filter(([, v]) => v != null && v !== '')) }
  return html.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => (Object.prototype.hasOwnProperty.call(values, key) ? esc(values[key]) : m))
}

// Hidden preview text; the filler stops the body copy leaking into the inbox
// preview line after it.
const preheaderHtml = (text) => {
  if (!text) return ''
  const filler = '&#8199;&#65279;&#847; '.repeat(70)
  return `<div class="es-preheader" style="display:none;font-size:1px;line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;mso-hide:all;">${esc(text)}${filler}</div>`
}

export const fontKeysFor = (theme) => [theme.type.display, theme.type.text, theme.type.italicFont]

// The complete email. Merge tags stay as {{Tags}} unless a sample is given.
export const renderEmail = (blocks, { subject = '', preheader, sample, builder = false, theme: themeOverride } = {}) => {
  const list = (blocks || []).filter(Boolean)
  const theme = themeOverride || themeForBlocks(list)
  const p = theme.palette
  const footer = list.find(b => b.type === 'footer')
  const pre = preheader != null ? preheader : (footer && footer.content && footer.content.preheader) || ''
  const bodyHtml = list.map(b => renderBlockHtml(b, theme, { builder })).join('\n')
  const fonts = googleFontsHref(fontKeysFor(theme))
  const html = `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta http-equiv="X-UA-Compatible" content="IE=edge">
<meta name="x-apple-disable-message-reformatting">
<meta name="format-detection" content="telephone=no, date=no, address=no, email=no, url=no">
<meta name="color-scheme" content="light only">
<meta name="supported-color-schemes" content="light only">
<title>${esc(subject)}</title>
<!--[if mso]><noscript><xml><o:OfficeDocumentSettings><o:AllowPNG/><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript><![endif]-->
${fonts ? `<!--[if !mso]><!--><link href="${esc(fonts)}" rel="stylesheet" type="text/css"><!--<![endif]-->` : ''}
<style>${emailStyleBlock()}</style>
<!--[if mso]><style>.es-f-display{font-family:${fontFallback(theme.type.display)} !important}.es-f-text{font-family:${fontFallback(theme.type.text)} !important}.es-f-italic{font-family:Georgia,'Times New Roman',serif !important}</style><![endif]-->
</head>
<body id="body" style="margin:0;padding:0;word-spacing:normal;background-color:${p.canvas};">
${preheaderHtml(pre)}
<div role="article" aria-roledescription="email" aria-label="${esc(subject)}" lang="en" style="background-color:${p.canvas};">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${p.canvas};"><tr><td align="center" class="es-outer" style="padding:28px 0;">
<!--[if mso]><table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" align="center"><tr><td><![endif]-->
<div style="max-width:600px;margin:0 auto;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;background-color:${p.paper};"><tr><td style="padding:0;">
${bodyHtml}
</td></tr></table>
</div>
<!--[if mso]></td></tr></table><![endif]-->
</td></tr></table>
</div>
</body>
</html>`
  return sample ? applySample(html, sample) : html
}

// Load a theme's web fonts into the app (canvas + previews) once each.
const loadedFontHrefs = new Set()
export const ensureFontsLoaded = (theme) => {
  if (typeof document === 'undefined' || !theme) return
  const href = googleFontsHref(fontKeysFor(theme))
  if (!href || loadedFontHrefs.has(href)) return
  loadedFontHrefs.add(href)
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  document.head.appendChild(link)
}

export { SECTIONS }
